import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listUsers, createUser, updateUser, deleteUser } from '../lib/api';
import PageHeader from '../components/layout/PageHeader';
import type { User } from '../types';

const ROLES: User['role'][] = ['admin', 'editor', 'viewer'];

const ROLE_COLORS: Record<User['role'], string> = {
  admin:  'bg-purple-100 text-purple-700',
  editor: 'bg-blue-100 text-blue-700',
  viewer: 'bg-gray-100 text-gray-700',
};

const ROLE_DESC: Record<User['role'], string> = {
  admin:  'Full access — can delete, manage settings and users',
  editor: 'Can add/edit items and issue/receive parts',
  viewer: 'Read-only access',
};

export default function Admin() {
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: listUsers,
  });

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<User['role']>('viewer');

  const add = useMutation({
    mutationFn: () => createUser(email, role),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); setEmail(''); },
  });

  const updateRole = useMutation({
    mutationFn: ({ id, role }: { id: number; role: string }) => updateUser(id, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Admin"
        breadcrumb={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Admin' }]}
      />

      <div className="flex-1 overflow-y-auto p-6 max-w-3xl space-y-6">
        {/* Role legend */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Role Permissions</h3>
          <div className="space-y-2">
            {ROLES.map((r) => (
              <div key={r} className="flex items-center gap-3">
                <span className={`badge ${ROLE_COLORS[r]} w-16 justify-center`}>{r}</span>
                <span className="text-sm text-gray-600">{ROLE_DESC[r]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Add user */}
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Add User</h3>
          <div className="flex gap-3">
            <input
              className="input flex-1"
              type="email"
              placeholder="user@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <select className="input w-32" value={role}
              onChange={(e) => setRole(e.target.value as User['role'])}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button
              className="btn-primary"
              disabled={!email || add.isPending}
              onClick={() => add.mutate()}
            >
              {add.isPending ? 'Adding…' : 'Add'}
            </button>
          </div>
          {add.isError && <p className="text-sm text-red-600 mt-2">{(add.error as Error).message}</p>}
        </div>

        {/* User list */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Users</h3>
            <span className="text-xs text-gray-500">{users.length} users</span>
          </div>

          {isLoading && <div className="p-4 text-sm text-gray-500">Loading…</div>}

          <div className="divide-y divide-gray-100">
            {users.map((user) => (
              <div key={user.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                <div>
                  <p className="text-sm font-medium text-gray-900">{user.email}</p>
                  <p className="text-xs text-gray-400">
                    Added {new Date(user.created_at).toLocaleDateString()}
                    {user.last_login && ` · Last login ${new Date(user.last_login).toLocaleDateString()}`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-brand-500 focus:outline-none"
                    value={user.role}
                    onChange={(e) => updateRole.mutate({ id: user.id, role: e.target.value })}
                  >
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <button
                    className="text-xs text-red-600 hover:text-red-700 font-medium"
                    onClick={() => {
                      if (confirm(`Remove ${user.email}?`)) remove.mutate(user.id);
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
